// One folder, and the boards in it.

import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api } from "../api.js";
import { BoardBrowser } from "../components/BoardBrowser.jsx";
import { EmptyState, ErrorNote, PageHead, Shell } from "../components/Shell.jsx";
import { useDialogs } from "../components/dialogs.jsx";
import { Menu } from "../ui/Menu.jsx";
import { Plus } from "../ui/icons.jsx";
import { BoardRowsSkeleton, Skeleton } from "../ui/Skeleton.jsx";
import { actorName, exactTime, relativeTime } from "../ui/format.js";

export const FolderPage = () => {
  const { folderId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    try {
      const [one, all] = await Promise.all([api.getFolder(folderId), api.listFolders()]);
      setData({ folder: one.folder, boards: one.boards, folders: all.folders });
      setError(null);
    } catch (err) {
      setError(err.message);
    }
  }, [folderId]);

  useEffect(() => {
    // Clear on the way in, so switching folders shows a placeholder rather than
    // the previous folder's boards under the new folder's name.
    setData(null);
    void load();
  }, [folderId, load]);

  const { open, element } = useDialogs({ folders: data?.folders ?? [], onChanged: load, navigate });

  const loading = data === null;
  const folder = data?.folder;

  return (
    <Shell
      crumbs={folder ? [{ label: folder.name }] : []}
      actions={
        <>
          <Menu
            label="Folder actions"
            className="btn ghost icon"
            items={
              loading
                ? []
                : [
                    { label: "Rename folder", onSelect: () => open("renameFolder", folder) },
                    "separator",
                    {
                      label: "Delete folder",
                      danger: true,
                      onSelect: () =>
                        open("deleteFolder", { ...folder, boardCount: data.boards.length }),
                    },
                  ]
            }
          />
          <button
            className="btn primary"
            onClick={() => open("newBoard", null, { folderId })}
            disabled={loading}
          >
            <Plus />
            New board
          </button>
        </>
      }
    >
      <ErrorNote error={error} onDismiss={() => setError(null)} />

      {loading ? (
        <div className="page-head">
          <div className="stack" style={{ gap: 10 }}>
            <Skeleton width={220} height={26} radius={8} />
            <Skeleton width={280} height={13} />
          </div>
        </div>
      ) : (
        <PageHead
          title={folder.name}
          lede={
            <>
              Created by {actorName(folder.createdByName, folder.createdByEmail, folder.createdBy)}{" "}
              <span title={exactTime(folder.createdAt)}>{relativeTime(folder.createdAt)}</span>
            </>
          }
        />
      )}

      {loading ? (
        <BoardRowsSkeleton />
      ) : (
        <BoardBrowser
          boards={data.boards}
          actions={{
            onRename: (board) => open("editBoard", board),
            onEditTags: (board) => open("editBoard", board, { focus: "tags" }),
            onMove: (board) => open("move", board),
            onDelete: (board) => open("deleteBoard", board),
          }}
          emptyState={
            <EmptyState
              title="No boards here yet"
              hint="An infinite canvas. Everyone can draw on it at the same time."
              action={
                <button
                  className="btn primary"
                  onClick={() => open("newBoard", null, { folderId })}
                >
                  New board
                </button>
              }
            />
          }
        />
      )}

      {element}
    </Shell>
  );
};
